import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ThemeProvider, alpha, createTheme } from "@mui/material/styles";
import {
  Brightness4,
  Brightness7,
  Menu as MenuIcon,
  School,
} from "@mui/icons-material";
import HomeSection from "./components/HomeSection";
import QuizSection from "./components/QuizSection";
import FlashcardSection from "./components/FlashcardSection";

const GRADES = [1, 2, 3, 4, 5, 6, 7, 8];
const MAX_QUIZ_QUESTIONS = 25;
const DEFAULT_GRADE = 1;
const DEFAULT_SUBJECT = "bharatanatyam";
const GRADE_STORAGE_KEY = "classicalArts.selectedGrade";
const SUBJECT_STORAGE_KEY = "classicalArts.selectedSubject";

const SUBJECTS = {
  bharatanatyam: { name: "Bharatanatyam", description: "Classical Indian Dance" },
  carnatic: { name: "Carnatic Music", description: "Classical Indian Music" },
};

const fetchJson = async (url) => {
  // Handle absolute paths by prepending the base URL
  const baseUrl = import.meta.env.BASE_URL || "/";
  const fullUrl = url.startsWith("/")
    ? `${baseUrl.replace(/\/$/, "")}${url}`
    : url;
  const response = await fetch(fullUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${fullUrl}`);
  }
  return response.json();
};

const normalizeFlashcard = (card, index) => {
  if (!card || typeof card !== "object") {
    return { id: `card-${index}`, front: "Untitled", back: "No content yet." };
  }

  const front = card.front ?? card.term ?? card.question ?? card.title;
  const back = card.back ?? card.definition ?? card.answer ?? card.description;

  return {
    id: card.id ?? `card-${index}`,
    front: front ?? "Untitled",
    back: back ?? "No content yet.",
  };
};

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = shuffled[i];
    shuffled[i] = shuffled[j];
    shuffled[j] = temp;
  }
  return shuffled;
};

const shuffleQuestionOptions = (question) => {
  if (!question || !question.options || !Array.isArray(question.options)) {
    return question;
  }

  // Only shuffle if shuffle_options is true (default to true if not specified)
  const shouldShuffle = question.shuffle_options !== false;

  if (!shouldShuffle) {
    return question;
  }

  return {
    ...question,
    options: shuffleArray(question.options),
  };
};

const getRandomQuestions = (questions) => {
  if (!Array.isArray(questions)) {
    return [];
  }

  // First shuffle the questions themselves
  const shuffledQuestions = shuffleArray(questions);

  // Then shuffle options for each question and take MAX_QUIZ_QUESTIONS
  return shuffledQuestions
    .slice(0, Math.min(MAX_QUIZ_QUESTIONS, shuffledQuestions.length))
    .map(shuffleQuestionOptions);
};

function App() {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const [mode, setMode] = useState(prefersDark ? "dark" : "light");
  const [modeLocked, setModeLocked] = useState(false);
  const isCompact = useMediaQuery("(max-width:900px)");
  const isMobile = useMediaQuery("(max-width:720px)");

  useEffect(() => {
    if (!modeLocked) {
      setMode(prefersDark ? "dark" : "light");
    }
  }, [prefersDark, modeLocked]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#007acc",
          },
          secondary: {
            main: "#4ec9b0",
          },
          background: {
            default: mode === "dark" ? "#1e1e1e" : "#ffffff",
            paper: mode === "dark" ? "#252526" : "#f3f3f3",
          },
          text: {
            primary: mode === "dark" ? "#d4d4d4" : "#1e1e1e",
            secondary: mode === "dark" ? "#9da1a7" : "#5a5a5a",
          },
          divider: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "#e0e0e0",
        },
        typography: {
          fontFamily:
            '"IBM Plex Sans", "Segoe UI", "Helvetica Neue", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
          },
          h2: {
            fontWeight: 700,
          },
          h3: {
            fontWeight: 700,
          },
          button: {
            textTransform: "none",
            fontWeight: 600,
          },
        },
        shape: {
          borderRadius: 16,
        },
      }),
    [mode],
  );

  const [selectedSubject, setSelectedSubject] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_SUBJECT;
    }

    try {
      const stored = window.localStorage.getItem(SUBJECT_STORAGE_KEY);
      return Object.keys(SUBJECTS).includes(stored) ? stored : DEFAULT_SUBJECT;
    } catch {
      return DEFAULT_SUBJECT;
    }
  });

  const [selectedGrade, setSelectedGrade] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_GRADE;
    }

    try {
      const stored = window.localStorage.getItem(GRADE_STORAGE_KEY);
      const parsed = Number(stored);
      return GRADES.includes(parsed) ? parsed : DEFAULT_GRADE;
    } catch {
      return DEFAULT_GRADE;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SUBJECT_STORAGE_KEY, selectedSubject);
    } catch {
      // Ignore storage write failures
    }
  }, [selectedSubject]);

  useEffect(() => {
    try {
      window.localStorage.setItem(GRADE_STORAGE_KEY, String(selectedGrade));
    } catch {
      // Ignore storage write failures (private mode, blocked storage).
    }
  }, [selectedGrade]);

  const [catalog, setCatalog] = useState(null);
  const [quizSets, setQuizSets] = useState([]);
  const [flashcardSets, setFlashcardSets] = useState([]);
  const [activeQuizIndex, setActiveQuizIndex] = useState(null);
  const [activeQuestions, setActiveQuestions] = useState([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answerLog, setAnswerLog] = useState({});
  const [activeDeckIndex, setActiveDeckIndex] = useState(null);
  const [studyIndex, setStudyIndex] = useState(0);
  const [studyOrder, setStudyOrder] = useState([]);
  const [studyFlipped, setStudyFlipped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeView, setActiveView] = useState("home");
  const [quizMode, setQuizMode] = useState("study"); // "study" or "exam"

  const navItems = useMemo(
    () => [
      { id: "home", label: "Home" },
      { id: "quizzes", label: "Quiz" },
      { id: "flashcards", label: "Flashcard" },
    ],
    [],
  );

  useEffect(() => {
    let isMounted = true;

    fetchJson("/data/manifest.json")
      .then((data) => {
        if (isMounted) {
          setCatalog(data);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError("Unable to load the data catalog.");
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!catalog) {
      return;
    }

    const gradeKey = String(selectedGrade);
    const subjectData = catalog?.subjects?.[selectedSubject];
    const quizEntries = subjectData?.quizzes?.[gradeKey] ?? [];
    const flashEntries = subjectData?.flashcards?.[gradeKey] ?? [];

    setLoading(true);
    setError("");
    setActiveQuizIndex(null);
    setQuestionIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
    setAnswerLog({});
    setActiveDeckIndex(null);
    setStudyIndex(0);
    setStudyOrder([]);
    setStudyFlipped(false);

    const quizPromises = quizEntries.map(async (entry) => {
      const data = await fetchJson(entry.file);
      // Handle both array format and metadata format
      const questions = Array.isArray(data) ? data : data.questions || [];
      return {
        title: entry.title ?? entry.topic ?? "Untitled quiz",
        topic: entry.topic ?? "",
        questions,
      };
    });

    const flashPromises = flashEntries.map(async (entry) => {
      const cards = await fetchJson(entry.file);
      return {
        title: entry.title ?? entry.topic ?? "Flashcards",
        topic: entry.topic ?? "",
        cards: Array.isArray(cards) ? cards.map(normalizeFlashcard) : [],
      };
    });

    Promise.all([Promise.all(quizPromises), Promise.all(flashPromises)])
      .then(([quizResults, flashResults]) => {
        setQuizSets(quizResults);
        setFlashcardSets(flashResults);
      })
      .catch(() => {
        setError("Some learning materials could not be loaded yet.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [catalog, selectedGrade, selectedSubject]);

  useEffect(() => {
    const questions = quizSets[activeQuizIndex]?.questions ?? [];
    setActiveQuestions(getRandomQuestions(questions));
    setQuestionIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
    setAnswerLog({});
  }, [activeQuizIndex, quizSets]);

  useEffect(() => {
    const deckCards = flashcardSets[activeDeckIndex]?.cards ?? [];
    // Automatically shuffle flashcards when deck is loaded
    const indices = deckCards.map((_, index) => index);
    setStudyOrder(shuffleArray(indices));
    setStudyIndex(0);
    setStudyFlipped(false);
  }, [activeDeckIndex, flashcardSets]);

  const activeQuiz = quizSets[activeQuizIndex];
  const questions = useMemo(() => activeQuestions, [activeQuestions]);
  const question = questions[questionIndex];
  const hasQuestions = questions.length > 0;
  const questionKey =
    question?.question_id ?? `${activeQuizIndex}-${questionIndex}`;

  const normalizeAnswerText = (value) =>
    String(value ?? "").trim().toLowerCase();
  const normalizeSentence = (value) =>
    normalizeAnswerText(value).replace(/[^a-z0-9]+/g, " ").trim();
  const normalizeTrueFalse = (value) => {
    if (typeof value === "boolean") {
      return value ? "True" : "False";
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "true") {
        return "True";
      }
      if (normalized === "false") {
        return "False";
      }
      return value.trim();
    }
    return null;
  };

  const isAnswerProvidedValue = (value) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "object") {
      return Object.keys(value).length > 0;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    return true;
  };

  const getCorrectOptions = (q) => {
    if (!q) {
      return [];
    }

    const raw = q.correct_answer;
    const type = q.question_type ?? "single_correct_mcq";

    if (type === "true_false") {
      if (Array.isArray(raw)) {
        return raw
          .map(normalizeTrueFalse)
          .filter((value) => value === "True" || value === "False");
      }
      const normalized = normalizeTrueFalse(raw);
      return normalized ? [normalized] : [];
    }

    if (
      type === "single_correct_mcq" ||
      type === "assertion_reason" ||
      type === "multiple_correct_mcq"
    ) {
      if (Array.isArray(raw)) {
        return raw;
      }
      if (typeof raw === "string") {
        return [raw];
      }
      return [];
    }

    return [];
  };

  const isAnswerCorrect = (q, answer) => {
    if (!q) {
      return false;
    }

    const type = q.question_type ?? "single_correct_mcq";

    if (type === "match_the_following") {
      const expected = q.correct_answer;
      if (
        !expected ||
        typeof expected !== "object" ||
        Array.isArray(expected) ||
        !answer ||
        typeof answer !== "object" ||
        Array.isArray(answer)
      ) {
        return false;
      }
      return Object.keys(expected).every(
        (key) => expected[key] === answer[key],
      );
    }

    if (type === "sequence_order") {
      const expected = q.correct_order;
      if (!Array.isArray(expected) || !Array.isArray(answer)) {
        return false;
      }
      if (expected.length !== answer.length) {
        return false;
      }
      return expected.every((item, index) => item === answer[index]);
    }

    if (type === "short_answer") {
      const expected = normalizeSentence(q.correct_answer);
      const provided = normalizeSentence(answer);
      if (!expected || !provided) {
        return false;
      }
      return expected.includes(provided) || provided.includes(expected);
    }

    if (type === "true_false") {
      const expected = getCorrectOptions(q).map(normalizeAnswerText);
      const provided = normalizeAnswerText(answer);
      return expected.includes(provided);
    }

    if (type === "multiple_correct_mcq") {
      const expected = getCorrectOptions(q).map(normalizeAnswerText);
      if (!Array.isArray(answer)) {
        return false;
      }
      const provided = answer.map(normalizeAnswerText);
      if (expected.length !== provided.length) {
        return false;
      }
      return expected.every((id) => provided.includes(id));
    }

    const expected = getCorrectOptions(q);
    return expected.includes(answer);
  };

  const correctOptions = getCorrectOptions(question);

  const activeDeck = flashcardSets[activeDeckIndex];
  const activeDeckCards = activeDeck?.cards ?? [];
  const orderedCards = studyOrder.length
    ? studyOrder.map((index) => activeDeckCards[index]).filter(Boolean)
    : activeDeckCards;
  const currentCard = orderedCards[studyIndex];

  useEffect(() => {
    if (!question) {
      setSelectedOption(null);
      setShowAnswer(false);
      return;
    }

    const stored = answerLog[questionKey];
    setSelectedOption(stored?.selectedOption ?? null);
    // Only show answer in study mode if it was checked
    setShowAnswer(quizMode === "study" && Boolean(stored?.isChecked));
  }, [questionKey, answerLog, question, quizMode]);

  const optionTextMap = useMemo(() => {
    if (!question?.options) {
      return {};
    }
    return question.options.reduce((acc, option, index) => {
      if (typeof option === "string") {
        acc[String.fromCharCode(65 + index)] = option;
      } else {
        acc[option.id] = option.text;
      }
      return acc;
    }, {});
  }, [question]);

  const correctAnswerText = correctOptions
    .map((id) => `${id}. ${optionTextMap[id] ?? ""}`.trim())
    .join(" / ");

  // For completion: count all selections in study mode, checked in exam mode
  const answersForCompletion = Object.values(answerLog).filter((entry) => {
    if (quizMode === "exam") {
      return entry.isChecked;
    }
    return isAnswerProvidedValue(entry.selectedOption);
  }).length;

  // For accuracy: only count checked answers in both modes
  const checkedAnswers = Object.values(answerLog).filter(
    (entry) => entry.isChecked,
  );
  const attemptedCount = checkedAnswers.length;
  const correctCount = checkedAnswers.filter((entry) => entry.isCorrect).length;
  const accuracy = attemptedCount
    ? Math.round((correctCount / attemptedCount) * 100)
    : 0;
  const progressPercent = questions.length
    ? Math.round((attemptedCount / questions.length) * 100)
    : 0;

  const handleSelectAndCheck = (answerValue) => {
    // For both modes: select and immediately record the answer
    setSelectedOption(answerValue);

    if (!question) {
      return;
    }

    const isCorrect = isAnswerCorrect(question, answerValue);
    setAnswerLog((prev) => ({
      ...prev,
      [questionKey]: {
        selectedOption: answerValue,
        isCorrect,
        isChecked: true,
      },
    }));

    // In study mode, show the answer feedback immediately
    if (quizMode === "study") {
      setShowAnswer(true);
    }
  };

  const handleCheckAnswer = (answerValue) => {
    if (!question) {
      return;
    }

    const finalAnswer = answerValue ?? selectedOption;
    if (!isAnswerProvidedValue(finalAnswer)) {
      return;
    }

    setSelectedOption(finalAnswer);

    const isCorrect = isAnswerCorrect(question, finalAnswer);
    setAnswerLog((prev) => ({
      ...prev,
      [questionKey]: {
        selectedOption: finalAnswer,
        isCorrect,
        isChecked: true,
      },
    }));

    if (quizMode === "study") {
      setShowAnswer(true);
    }
  };

  const handleSelectOption = (answerValue) => {
    // Update the selected option and save to answerLog (without isChecked flag)
    setSelectedOption(answerValue);

    // Save selection to answerLog so it persists on navigation
    setAnswerLog((prev) => ({
      ...prev,
      [questionKey]: {
        selectedOption: answerValue,
        // Preserve isCorrect and isChecked if they already exist
        ...(prev[questionKey]?.isCorrect !== undefined && {
          isCorrect: prev[questionKey].isCorrect,
        }),
        ...(prev[questionKey]?.isChecked && {
          isChecked: prev[questionKey].isChecked,
        }),
      },
    }));
  };

  const handleResetQuiz = () => {
    const questions = activeQuiz?.questions ?? [];
    setActiveQuestions(getRandomQuestions(questions));
    setAnswerLog({});
    setQuestionIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
  };

  const handleEndQuiz = () => {
    setAnswerLog({});
    setQuestionIndex(0);
    setSelectedOption(null);
    setShowAnswer(false);
    setActiveQuizIndex(null);
    setQuizMode("study"); // Reset to study mode
  };

  const handleQuizModeChange = (newMode) => {
    setQuizMode(newMode);
    setShowAnswer(false);
    // Reset answer log when switching modes to prevent confusion
    setAnswerLog({});
    setQuestionIndex(0);
    setSelectedOption(null);
  };

  const handlePrevQuestion = () => {
    setQuestionIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleNextQuestion = () => {
    setQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const handleShuffleDeck = () => {
    if (activeDeckCards.length === 0) {
      return;
    }

    const order = activeDeckCards.map((_, index) => index);
    setStudyOrder(shuffleArray(order));
    setStudyIndex(0);
    setStudyFlipped(false);
  };

  const quizComplete =
    questions.length > 0 && answersForCompletion === questions.length;

  const handleNavChange = (view) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  const handlePrevCard = () => {
    setStudyIndex((prev) => Math.max(prev - 1, 0));
    setStudyFlipped(false);
  };

  const handleNextCard = () => {
    setStudyIndex((prev) => Math.min(prev + 1, orderedCards.length - 1));
    setStudyFlipped(false);
  };

  const handleToggleFlip = () => {
    setStudyFlipped((prev) => !prev);
  };

  const navButtonSx = (isActive) => (themeValue) => ({
    borderRadius: 999,
    px: 1.6,
    fontWeight: isActive ? 700 : 600,
    color: isActive
      ? themeValue.palette.primary.main
      : themeValue.palette.text.primary,
    bgcolor: isActive
      ? alpha(themeValue.palette.primary.main, mode === "dark" ? 0.2 : 0.12)
      : "transparent",
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          backgroundImage:
            mode === "dark"
              ? "radial-gradient(circle at top, rgba(0, 122, 204, 0.18), rgba(30, 30, 30, 0.95) 60%)"
              : "radial-gradient(circle at top, rgba(0, 122, 204, 0.12), rgba(255, 255, 255, 0.85) 55%)",
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor:
              mode === "dark"
                ? alpha(theme.palette.background.default, 0.85)
                : "rgba(243, 243, 243, 0.98)",
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
            color: theme.palette.text.primary,
          }}
        >
          <Toolbar sx={{ gap: { xs: 1, sm: 2 } }}>
            <School color="primary" />
            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                cursor: "pointer",
              }}
              onClick={() => handleNavChange("home")}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                }}
              >
                Classical Arts Learning
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {SUBJECTS[selectedSubject]?.name} · Grade {selectedGrade}
              </Typography>
            </Box>
            {!isMobile && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: "wrap" }}
              >
                {navItems.map((item) => (
                  <Button
                    key={item.id}
                    color="inherit"
                    onClick={() => handleNavChange(item.id)}
                    sx={navButtonSx(activeView === item.id)}
                  >
                    {item.label}
                  </Button>
                ))}
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <Select
                    value={selectedSubject}
                    onChange={(event) => setSelectedSubject(event.target.value)}
                    inputProps={{ "aria-label": "Select subject" }}
                  >
                    {Object.entries(SUBJECTS).map(([key, subject]) => (
                      <MenuItem key={key} value={key}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <Select
                    value={selectedGrade}
                    onChange={(event) =>
                      setSelectedGrade(Number(event.target.value))
                    }
                    renderValue={(value) => `Grade ${value}`}
                    inputProps={{ "aria-label": "Select grade" }}
                  >
                    {GRADES.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center">
              {!isMobile && (
                <Typography variant="body2" color="text.secondary">
                  {mode === "dark" ? "Dark" : "Light"} mode
                </Typography>
              )}
              {!isMobile && (
                <IconButton
                  color="primary"
                  onClick={() => {
                    setMode((prev) => (prev === "dark" ? "light" : "dark"));
                    setModeLocked(true);
                  }}
                  aria-label="toggle theme"
                >
                  {mode === "dark" ? <Brightness7 /> : <Brightness4 />}
                </IconButton>
              )}
              {isMobile && (
                <IconButton
                  color="primary"
                  onClick={() => setMobileNavOpen(true)}
                  aria-label="open navigation"
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          </Toolbar>
        </AppBar>

        <Drawer
          anchor="right"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        >
          <Box sx={{ width: 280, p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Navigate
              </Typography>
              <List disablePadding>
                {navItems.map((item) => (
                  <ListItemButton
                    key={item.id}
                    onClick={() => handleNavChange(item.id)}
                    selected={activeView === item.id}
                  >
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                ))}
              </List>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Subject
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedSubject}
                    onChange={(event) => setSelectedSubject(event.target.value)}
                    inputProps={{ "aria-label": "Select subject" }}
                  >
                    {Object.entries(SUBJECTS).map(([key, subject]) => (
                      <MenuItem key={key} value={key}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Grade
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedGrade}
                    onChange={(event) =>
                      setSelectedGrade(Number(event.target.value))
                    }
                    renderValue={(value) => `Grade ${value}`}
                    inputProps={{ "aria-label": "Select grade" }}
                  >
                    {GRADES.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Divider />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Theme
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant={mode === "light" ? "contained" : "outlined"}
                    size="small"
                    onClick={() => {
                      setMode("light");
                      setModeLocked(true);
                    }}
                    startIcon={<Brightness7 />}
                    fullWidth
                  >
                    Light
                  </Button>
                  <Button
                    variant={mode === "dark" ? "contained" : "outlined"}
                    size="small"
                    onClick={() => {
                      setMode("dark");
                      setModeLocked(true);
                    }}
                    startIcon={<Brightness4 />}
                    fullWidth
                  >
                    Dark
                  </Button>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Drawer>

        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
          {activeView === "home" && (
            <HomeSection
              isCompact={isCompact}
              onSelectView={handleNavChange}
              quizCount={quizSets.length}
              flashcardCount={flashcardSets.length}
              loading={loading}
            />
          )}
          {activeView === "quizzes" && (
            <QuizSection
              loading={loading}
              error={error}
              quizSets={quizSets}
              activeQuizIndex={activeQuizIndex}
              onSelectQuiz={setActiveQuizIndex}
              hasQuestions={hasQuestions}
              question={question}
              questionIndex={questionIndex}
              questions={questions}
              maxQuestions={MAX_QUIZ_QUESTIONS}
              selectedOption={selectedOption}
              onSelectOption={handleSelectOption}
              showAnswer={showAnswer}
              onHideAnswer={() => setShowAnswer(false)}
              answerLog={answerLog}
              questionKey={questionKey}
              correctOptions={correctOptions}
              correctAnswerText={correctAnswerText}
              progressPercent={progressPercent}
              attemptedCount={attemptedCount}
              correctCount={correctCount}
              accuracy={accuracy}
              onResetQuiz={handleResetQuiz}
              onEndQuiz={handleEndQuiz}
              onPrevQuestion={handlePrevQuestion}
              onNextQuestion={handleNextQuestion}
              quizComplete={quizComplete}
              mode={mode}
              quizMode={quizMode}
              onQuizModeChange={handleQuizModeChange}
              onSelectAndCheck={handleSelectAndCheck}
              onCheckAnswer={handleCheckAnswer}
            />
          )}
          {activeView === "flashcards" && (
            <FlashcardSection
              loading={loading}
              flashcardSets={flashcardSets}
              activeDeckIndex={activeDeckIndex}
              onSelectDeck={setActiveDeckIndex}
              activeDeck={activeDeck}
              activeDeckCards={activeDeckCards}
              orderedCards={orderedCards}
              currentCard={currentCard}
              studyIndex={studyIndex}
              studyFlipped={studyFlipped}
              onToggleFlip={handleToggleFlip}
              onPrevCard={handlePrevCard}
              onNextCard={handleNextCard}
              onShuffle={handleShuffleDeck}
            />
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
}

export default App;
